import { styled } from "goober";
import { useMemo } from "react";
import { AskMonitoringChat } from "./AskMonitoringChat";
import { connectorsByIdentifier } from "../../../shared/connectors";
import {
  computeWorkflowDependencies,
  workflowNamesByDependency,
} from "../../../shared/monitoringWorkflowDependencies";

import { useSpaceDisplayName } from "../../hooks/useSpaceDisplayName";
import { useMakeUrl, navigate, routes, useCurrentUrl } from "../../lib/urls";
import { useMonitoringInteraction } from "./analytics";
import { Button } from "../Button";
import { ConnectorLogo } from "../ConnectorLogo";
import { ConnectorLogoContainer } from "../ConnectorLogoContainer";
import { FacePile } from "../FacePile";
import { ListTable } from "../ListTable";
import { Modal } from "../Modal";
import { PressButton } from "../PressButton";
import { OverflowBadges } from "../Settings/OverflowBadges";
import { PillBadge, SearchField } from "../Settings/shared";
import { Text } from "../Text";
import { Tooltip } from "../Tooltip";
import { WorkflowBadge } from "../WorkflowBadge";
import {
  DETAIL_MODAL_STYLE,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalHeaderRight,
  ModalTitle,
  SummaryCard,
} from "./MonitoringDetailCard";
import { ListSection, NameBody, NameCell } from "./monitoringList";
import {
  useMonitoringSpaces,
  useMonitoringWorkflowDependencies,
  useMonitoringWorkflows,
} from "./useMonitoringWorkflows";
import { PanelSkeleton } from "./workflowsLayout";

const Tabs = styled("div")`
  display: flex;
  flex-shrink: 0;
  gap: 8px;
`;

const TabButton = styled(PressButton)`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  padding: 8px 12px;
  border-radius: 24px;
  color: var(--text-secondary);
  background: var(--surface-primary);

  &.active,
  &:hover {
    color: var(--text-primary);
    background:
      linear-gradient(var(--alpha4), var(--alpha4)), var(--surface-primary);
  }
`;

const GhostFace = styled("div")`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: var(--alpha8);
`;

type View = "packages" | "workflows";

export function WorkflowDependenciesPanel() {
  const makeUrl = useMakeUrl();
  const recordInteraction = useMonitoringInteraction();
  const { report, loading } = useMonitoringWorkflows();
  const { spaceById, loading: spacesLoading } = useMonitoringSpaces();
  const displayName = useSpaceDisplayName();
  const {
    panel,
    view: viewParam,
    search: searchParam,
  } = useCurrentUrl(routes.monitoring.workflows);
  const open = panel === "dependencies";
  const {
    workflows: dependencyWorkflows,
    loading: dependencyWorkflowsLoading,
  } = useMonitoringWorkflowDependencies(open);
  const view: View = viewParam === "workflows" ? "workflows" : "packages";
  const search = searchParam ?? "";
  const setView = (next: View) =>
    navigate(
      routes.monitoring.workflows,
      { view: next, search: "" },
      { replace: true }
    );
  const setSearch = (value: string) =>
    navigate(routes.monitoring.workflows, { search: value }, { replace: true });
  const close = () =>
    navigate(
      routes.monitoring.workflows,
      { panel: "", view: "", search: "" },
      { replace: true }
    );

  const workflowRows = useMemo(
    () => computeWorkflowDependencies(dependencyWorkflows),
    [dependencyWorkflows]
  );

  const summaryRows = report.dependencySummary;

  const workflowNamesByPackage = useMemo(
    () => workflowNamesByDependency(workflowRows),
    [workflowRows]
  );

  const filteredSummaryRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return summaryRows;
    return summaryRows.filter((row) => row.name.toLowerCase().includes(q));
  }, [summaryRows, search]);

  const filteredWorkflowRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return workflowRows;
    return workflowRows.filter((row) => row.name.toLowerCase().includes(q));
  }, [workflowRows, search]);

  const totalPackages = summaryRows.length;
  const multiVersion = summaryRows.filter((r) => r.versions.length > 1).length;

  const packageStack =
    totalPackages > 0 ? (
      <FacePile
        itemSize={28}
        outlineWidth={2}
        cutout
        items={summaryRows.map((row) => ({
          id: row.name,
          label: row.name,
          node: packageLogoNode(row.name, row.source),
          bare: true,
        }))}
      />
    ) : (
      <div aria-hidden>
        <FacePile
          itemSize={28}
          outlineWidth={2}
          cutout
          items={["ghost-1", "ghost-2", "ghost-3"].map((id) => ({
            id,
            node: <GhostFace />,
            bare: true,
          }))}
        />
      </div>
    );

  if (loading || spacesLoading) {
    return <PanelSkeleton />;
  }

  return (
    <>
      <SummaryCard
        title="Workflow dependencies"
        cornerAction={
          <AskMonitoringChat
            surface="dependencies"
            context={{
              scope: "both",
              filters: { spaceId: null, view: "packages" },
            }}
            snapshot={`${totalPackages} packages; ${multiVersion} with multiple declared versions`}
          />
        }
        stacked
        onOpen={
          totalPackages > 0
            ? () => {
                recordInteraction("workflows.panel_drilldown_open", {
                  value: "dependencies",
                });
                navigate(routes.monitoring.workflows, {
                  panel: "dependencies",
                  search: "",
                });
              }
            : undefined
        }
        accessory={packageStack}
      />

      <Modal open={open} onClose={close} style={DETAIL_MODAL_STYLE}>
        <ModalHeader>
          <ModalTitle icon="package-2" title="Workflow dependencies" />
          <Tabs>
            <TabButton
              className={view === "packages" ? "active" : undefined}
              aria-pressed={view === "packages"}
              onPress={() => {
                recordInteraction("workflows.dependencies_view_toggle", {
                  value: "packages",
                });
                setView("packages");
              }}
            >
              <Text variant="text-small-md">By package</Text>
            </TabButton>
            <TabButton
              className={view === "workflows" ? "active" : undefined}
              aria-pressed={view === "workflows"}
              onPress={() => {
                recordInteraction("workflows.dependencies_view_toggle", {
                  value: "workflows",
                });
                setView("workflows");
              }}
            >
              <Text variant="text-small-md">By workflow</Text>
            </TabButton>
          </Tabs>
          <ModalHeaderRight>
            <AskMonitoringChat
              surface="dependencies"
              alwaysVisible
              inline
              includeSearch
              disabled={view === "workflows" && dependencyWorkflowsLoading}
              context={{ scope: "both", filters: { spaceId: null, view } }}
              snapshot={
                view === "packages"
                  ? `${filteredSummaryRows.length} packages in the filtered table`
                  : `${filteredWorkflowRows.length} workflows in the filtered table`
              }
            />
            <Button size="l" variant="secondary" icon="x" onClick={close} />
          </ModalHeaderRight>
        </ModalHeader>

        <ModalBody style={{ gap: 0 }}>
          <ListSection
            title={view === "packages" ? "Packages" : "Workflows"}
            description={
              view === "packages"
                ? "External packages used across your live workflows."
                : "Dependencies declared in each workflow."
            }
            action={
              <SearchField
                placeholder={
                  view === "packages" ? "Search packages…" : "Search workflows…"
                }
                value={search}
                onChange={setSearch}
                width={280}
                autoFocus
              />
            }
          >
            {view === "packages" ? (
              <ListTable
                headers={{
                  name: "Package",
                  version: "Version",
                  used: "Used by",
                  source: "Source",
                }}
                columns={{
                  name: "flex",
                  version: "md",
                  used: "md",
                  source: "md",
                }}
                emptyState={
                  <Text variant="text-regular" color="var(--text-secondary)">
                    {search
                      ? "No packages match your search."
                      : "No external packages detected."}
                  </Text>
                }
                items={filteredSummaryRows.map((row) => ({
                  key: row.name,
                  name: (
                    <NameCell>
                      <PackageBadge name={row.name} source={row.source} />
                      <Text variant="text-regular-md" ellipsis>
                        {row.name}
                      </Text>
                    </NameCell>
                  ),
                  version:
                    row.versions.length > 0 ? (
                      <OverflowBadges
                        items={row.versions}
                        keyFn={(v) => v}
                        renderBadge={(v) => <PillBadge>{v}</PillBadge>}
                        overflowTooltip={(hidden) => hidden.join(", ")}
                      />
                    ) : (
                      <Text variant="text-small" color="var(--text-secondary)">
                        —
                      </Text>
                    ),
                  used: (
                    <Tooltip
                      content={(
                        workflowNamesByPackage.get(row.name) ?? []
                      ).join(", ")}
                    >
                      <Text variant="text-small">{row.workflowCount}</Text>
                    </Tooltip>
                  ),
                  source: <PillBadge>{sourceLabel(row.source)}</PillBadge>,
                }))}
              />
            ) : (
              <ListTable
                headers={{ name: "Workflow", deps: "Dependencies" }}
                columns={{ name: "flex", deps: "flex" }}
                emptyState={
                  dependencyWorkflowsLoading ? null : (
                    <Text variant="text-regular" color="var(--text-secondary)">
                      {search
                        ? "No workflows match your search."
                        : "No external packages detected in workflow steps."}
                    </Text>
                  )
                }
                items={filteredWorkflowRows.map((row) => {
                  const space = spaceById.get(row.spaceId);
                  const branches = [{ name: "main" }];
                  return {
                    key: row.workflowId,
                    link: makeUrl(routes.space.workflow, {
                      spaceId: row.spaceId,
                      workflowId: row.workflowId,
                    }),
                    onNavigate: () =>
                      recordInteraction("workflows.workflow_link_click", {
                        targetType: "workflow",
                        targetId: row.workflowId,
                      }),
                    name: (hover: boolean) => (
                      <NameCell>
                        <WorkflowBadge
                          workflow={{
                            id: row.workflowId,
                            badgeSeed: row.badgeSeed,
                            pausedAt: row.pausedAt,
                            branches,
                          }}
                          hover={hover}
                        />
                        <NameBody>
                          <Text variant="text-regular-md" ellipsis>
                            {row.name}
                          </Text>
                          {space && (
                            <Text
                              variant="text-small"
                              color="var(--text-secondary)"
                              ellipsis
                            >
                              {displayName(space)}
                            </Text>
                          )}
                        </NameBody>
                      </NameCell>
                    ),
                    deps: (
                      <OverflowBadges
                        items={row.dependencies}
                        keyFn={(dep) => dep.name}
                        renderBadge={(dep) => (
                          <Tooltip
                            content={`${dep.name}${dep.version ? ` ${dep.version}` : ""} (${sourceLabel(dep.source)})`}
                          >
                            <PillBadge>
                              {dep.name}
                              {dep.version ? ` ${dep.version}` : ""}
                            </PillBadge>
                          </Tooltip>
                        )}
                        overflowTooltip={(hidden) =>
                          hidden
                            .map(
                              (d) =>
                                `${d.name}${d.version ? ` ${d.version}` : ""}`
                            )
                            .join(", ")
                        }
                      />
                    ),
                  };
                })}
              />
            )}
          </ListSection>
        </ModalBody>

        <ModalFooter>
          <Text variant="text-small" color="var(--text-secondary)">
            {multiVersion > 0
              ? `${multiVersion} ${multiVersion === 1 ? "package has" : "packages have"} multiple versions`
              : `${totalPackages} ${totalPackages === 1 ? "package" : "packages"}`}
          </Text>
          <Button size="l" variant="primary" onClick={close}>
            Done
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}

function packageBadgeKey(name: string, source: string): string {
  if (source === "Dockerfile") {
    return name.split(":")[0]!.split("/").pop()!;
  }
  return name.replace(/^@/, "").split("/")[0]!;
}

function packageLogoContent(name: string, source: string, size: number) {
  const key = packageBadgeKey(name, source);
  const logo = connectorsByIdentifier[key]?.logo;
  return logo ? (
    <img
      alt={`${name} logo`}
      src={logo}
      style={{ width: size, height: size, flexShrink: 0 }}
    />
  ) : (
    <ConnectorLogo template={{ name: key, identifier: key }} size={size} />
  );
}

function PackageBadge({ name, source }: { name: string; source: string }) {
  return (
    <ConnectorLogoContainer
      size={32}
      borderRadius={8}
      outline={false}
      hoverBackground={false}
    >
      {packageLogoContent(name, source, 16)}
    </ConnectorLogoContainer>
  );
}

function packageLogoNode(name: string, source: string) {
  return (
    <Tooltip content={name}>
      <ConnectorLogoContainer
        size={28}
        borderRadius={14}
        outline={false}
        outlineWidth={2}
        background
        hoverBackground={false}
      >
        {packageLogoContent(name, source, 16)}
      </ConnectorLogoContainer>
    </Tooltip>
  );
}

function sourceLabel(source: string): string {
  if (source === "requirements.txt" || source === "pyproject.toml") {
    return "pip";
  }
  if (source === "Dockerfile") return "docker";
  return "pkg";
}
